import { create } from '@bufbuild/protobuf';

import type {
  CallPermissions,
  ParticipantIdToRole,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_model_pb';

import { wsCallsRequestManager } from '../WsCallsApi';
import {
  AddMembersToCallRequest,
  AddMembersToCallRequestSchema,
  ClearPermissionsForMemberRequest,
  ClearPermissionsForMemberRequestSchema,
  CreatePermissionRequestRequest,
  CreatePermissionRequestRequestSchema,
  FinishForAllRequest,
  FinishForAllRequestSchema,
  ForceMuteRequest,
  ForceMuteRequestSchema,
  JoinToCallRequest,
  JoinToCallRequestSchema,
  GetCallMembersByCameraOrScreenIdsRequest,
  GetCallMembersByCameraOrScreenIdsRequestSchema,
  GetCallMembersRequest,
  GetCallMembersRequestSchema,
  GetCallPermissionsRequest,
  GetCallPermissionsRequestSchema,
  GetPermissionRequestsRequest,
  GetPermissionRequestsRequestSchema,
  KickMemberRequest,
  KickMemberRequestSchema,
  RemoveMembersFromCallRequest,
  RemoveMembersFromCallRequestSchema,
  SetCallPermissionsForAllRequest,
  SetCallPermissionsForAllRequestSchema,
  SetCallPermissionsForMemberRequest,
  SetCallPermissionsForMemberRequestSchema,
  StartCallRecordingRequest,
  StartCallRecordingRequestSchema,
  StopCallRecordingRequest,
  StopCallRecordingRequestSchema,
  UpdatePermissionRequestForAllRequest,
  UpdatePermissionRequestForAllRequestSchema,
  UpdatePermissionRequestForMemberRequest,
  UpdatePermissionRequestForMemberRequestSchema,
  GetCallLinkRequest,
  GetCallLinkRequestSchema,
  UpdateWaitingRoomForMemberRequest,
  UpdateWaitingRoomForMemberRequestSchema,
  UpdateWaitingRoomForAllRequest,
  UpdateWaitingRoomForAllRequestSchema,
  WaitingRoomAccessRequest,
  WaitingRoomAccessRequestSchema,
  DeclineCallRequest,
  DeclineCallRequestSchema,
  SetCallParticipantsRolesRequest,
  SetCallParticipantsRolesRequestSchema,
  GetCallMembersByIdsRequest,
  GetCallMembersByIdsRequestSchema,
} from '../types/call/call_requests_pb';
import {
  AddMembersToCallResponse,
  ClearPermissionsForMemberResponse,
  CreatePermissionRequestResponse,
  FinishForAllResponse,
  ForceMuteResponse,
  JoinToCallResponse,
  GetCallMembersByCameraOrScreenIdsResponse,
  GetCallMembersResponse,
  GetCallPermissionsResponse,
  GetPermissionRequestsResponse,
  KickMemberResponse,
  RemoveMembersFromCallResponse,
  SetCallPermissionsForAllResponse,
  SetCallPermissionsForMemberResponse,
  StartCallRecordingResponse,
  StopCallRecordingResponse,
  UpdatePermissionRequestForAllResponse,
  UpdatePermissionRequestForMemberResponse,
  GetCallLinkResponse,
  UpdateWaitingRoomForMemberResponse,
  UpdateWaitingRoomForAllResponse,
  WaitingRoomAccessResponse,
  DeclineCallResponse,
  SetCallParticipantsRolesResponse,
  GetCallMembersByIdsResponse,
} from '../types/call/call_responses_pb';
import { WsRequest } from './request.types';

export function joinToCallRequest(data: WsRequest<JoinToCallRequest>): Promise<JoinToCallResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<JoinToCallResponse>({
    case: 'joinToCall',
    value: create(JoinToCallRequestSchema, data),
  });
}

export function finishCallForAllRequest(
  data: WsRequest<FinishForAllRequest>
): Promise<FinishForAllResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<FinishForAllResponse>({
    case: 'finishForAll',
    value: create(FinishForAllRequestSchema, data),
  });
}

export function kickMemberRequest(data: WsRequest<KickMemberRequest>): Promise<KickMemberResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<KickMemberResponse>({
    case: 'kickMember',
    value: create(KickMemberRequestSchema, data),
  });
}

export function forceMuteRequest(data: WsRequest<ForceMuteRequest>): Promise<ForceMuteResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<ForceMuteResponse>({
    case: 'forceMute',
    value: create(ForceMuteRequestSchema, data),
  });
}

export function startRecordingRequest(
  data: WsRequest<StartCallRecordingRequest>
): Promise<StartCallRecordingResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<StartCallRecordingResponse>({
    case: 'startCallRecording',
    value: create(StartCallRecordingRequestSchema, data),
  });
}

export function stopRecordingRequest(
  data: WsRequest<StopCallRecordingRequest>
): Promise<StopCallRecordingResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<StopCallRecordingResponse>({
    case: 'stopCallRecording',
    value: create(StopCallRecordingRequestSchema, data),
  });
}

export function getCallMembersRequest(
  data: WsRequest<GetCallMembersRequest>
): Promise<GetCallMembersResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<GetCallMembersResponse>({
    case: 'getCallMembers',
    value: create(GetCallMembersRequestSchema, data),
  });
}

export function getCallMembersByUidRequest(
  data: WsRequest<GetCallMembersByCameraOrScreenIdsRequest>
): Promise<GetCallMembersByCameraOrScreenIdsResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<GetCallMembersByCameraOrScreenIdsResponse>({
    case: 'getCallMembersByCameraOrScreenIds',
    value: create(GetCallMembersByCameraOrScreenIdsRequestSchema, data),
  });
}

export function getCallMembersByIdsRequest(
  data: WsRequest<GetCallMembersByIdsRequest>
): Promise<GetCallMembersByIdsResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<GetCallMembersByIdsResponse>({
    case: 'getCallMembersByIds',
    value: create(GetCallMembersByIdsRequestSchema, data),
  });
}

export function getCallPermissionsRequest(
  data: WsRequest<GetCallPermissionsRequest>
): Promise<GetCallPermissionsResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<GetCallPermissionsResponse>({
    case: 'getCallPermissions',
    value: create(GetCallPermissionsRequestSchema, data),
  });
}

export function getCallPermissionRequestsRequest(
  data: WsRequest<GetPermissionRequestsRequest>
): Promise<GetPermissionRequestsResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<GetPermissionRequestsResponse>({
    case: 'getPermissionRequests',
    value: create(GetPermissionRequestsRequestSchema, data),
  });
}

export function clearCallPermissionsForMemberRequest(
  data: WsRequest<ClearPermissionsForMemberRequest>
): Promise<ClearPermissionsForMemberResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<ClearPermissionsForMemberResponse>({
    case: 'clearPermissionsForMember',
    value: create(ClearPermissionsForMemberRequestSchema, data),
  });
}

export function createCallPermissionRequestRequest(
  data: WsRequest<CreatePermissionRequestRequest>
): Promise<CreatePermissionRequestResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<CreatePermissionRequestResponse>({
    case: 'createPermissionRequest',
    value: create(CreatePermissionRequestRequestSchema, data),
  });
}

export function setCallPermissionsForAllRequest(
  data: WsRequest<
    Omit<SetCallPermissionsForAllRequest, 'default'> & { default: WsRequest<CallPermissions> }
  >
): Promise<SetCallPermissionsForAllResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<SetCallPermissionsForAllResponse>({
    case: 'setCallPermissionsForAll',
    value: create(SetCallPermissionsForAllRequestSchema, data),
  });
}

export function setCallPermissionsForMemberRequest(
  data: WsRequest<SetCallPermissionsForMemberRequest>
): Promise<SetCallPermissionsForMemberResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<SetCallPermissionsForMemberResponse>({
    case: 'setCallPermissionsForMember',
    value: create(SetCallPermissionsForMemberRequestSchema, data),
  });
}

export function updateCallPermissionRequestForAllRequest(
  data: WsRequest<UpdatePermissionRequestForAllRequest>
): Promise<UpdatePermissionRequestForAllResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<UpdatePermissionRequestForAllResponse>({
    case: 'updatePermissionRequestForAll',
    value: create(UpdatePermissionRequestForAllRequestSchema, data),
  });
}

export function updateCallPermissionRequestForMemberRequest(
  data: WsRequest<UpdatePermissionRequestForMemberRequest>
): Promise<UpdatePermissionRequestForMemberResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<UpdatePermissionRequestForMemberResponse>({
    case: 'updatePermissionRequestForMember',
    value: create(UpdatePermissionRequestForMemberRequestSchema, data),
  });
}

export function addMembersToCallRequest(
  data: WsRequest<AddMembersToCallRequest>
): Promise<AddMembersToCallResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<AddMembersToCallResponse>({
    case: 'addMembersToCall',
    value: create(AddMembersToCallRequestSchema, data),
  });
}

export function removeMembersFromCallRequest(
  data: WsRequest<RemoveMembersFromCallRequest>
): Promise<RemoveMembersFromCallResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<RemoveMembersFromCallResponse>({
    case: 'removeMembersFromCall',
    value: create(RemoveMembersFromCallRequestSchema, data),
  });
}

export function getCallLinkRequest(
  data: WsRequest<GetCallLinkRequest>
): Promise<GetCallLinkResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }

  return wsCallsRequestManager.request<GetCallLinkResponse>({
    case: 'getCallLink',
    value: create(GetCallLinkRequestSchema, data),
  });
}

export function updateWaitingRoomForMemberRequest(
  data: WsRequest<UpdateWaitingRoomForMemberRequest>
): Promise<UpdateWaitingRoomForMemberResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<UpdateWaitingRoomForMemberResponse>({
    case: 'updateWaitingRoomForMember',
    value: create(UpdateWaitingRoomForMemberRequestSchema, data),
  });
}

export function updateWaitingRoomForAllRequest(
  data: WsRequest<UpdateWaitingRoomForAllRequest>
): Promise<UpdateWaitingRoomForAllResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<UpdateWaitingRoomForAllResponse>({
    case: 'updateWaitingRoomForAll',
    value: create(UpdateWaitingRoomForAllRequestSchema, data),
  });
}

export function waitingRoomAccessRequest(
  data: WsRequest<WaitingRoomAccessRequest>
): Promise<WaitingRoomAccessResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<WaitingRoomAccessResponse>({
    case: 'waitingRoomAccess',
    value: create(WaitingRoomAccessRequestSchema, data),
  });
}

export function declineCallRequest(
  data: WsRequest<DeclineCallRequest>
): Promise<DeclineCallResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<DeclineCallResponse>({
    case: 'declineCall',
    value: create(DeclineCallRequestSchema, data),
  });
}

export function setCallParticipantsRolesRequest(
  data: WsRequest<
    Omit<SetCallParticipantsRolesRequest, 'participantToRole'> & {
      participantToRole: WsRequest<ParticipantIdToRole>[];
    }
  >
): Promise<SetCallParticipantsRolesResponse> {
  if (!wsCallsRequestManager) {
    throw new Error('wsCallsRequestManager should be initialized');
  }
  return wsCallsRequestManager.request<SetCallParticipantsRolesResponse>({
    case: 'setCallParticipantsRoles',
    value: create(SetCallParticipantsRolesRequestSchema, data),
  });
}
